import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import {
  groupReadyBySong,
  joinDetail,
  madeText,
  POSTS_LANE_PAGE,
  PostsBoardFile,
  rolloutDayText,
  songHeading,
} from './postsModel';
import { postsMessages } from './postsMessages';
import { PostsLaneProps } from './postsLaneProps';
import { PostsFileRow } from './PostsFileRow';
import { PostsLane } from './PostsLane';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsReadyLaneProps extends PostsLaneProps {
  files: PostsBoardFile[];
}

/**
 * Ready to post (design assets-rethink c1): the ready files that no post uses, by song, with "Plan
 * them" per song. An AI reel in review shows the Review chip and "Review" (decision 7).
 */
const PostsReadyLaneComponent: React.FC<PostsReadyLaneProps> = (props) => {
  const { files, songs, now, actions, readOnly, selectionOf, onOpen, onArchive, isHiddenOnPhone } = props;
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const shown = useMemo(() => (isExpanded ? files : files.slice(0, POSTS_LANE_PAGE)), [files, isExpanded]);
  const groups = useMemo(() => groupReadyBySong(shown), [shown]);

  return (
    <PostsLane
      title={intl.formatMessage(postsMessages.laneReady)}
      pillClass={styles.pillReady}
      count={files.length}
      emptyText={intl.formatMessage(postsMessages.emptyReady)}
      hiddenCount={files.length - shown.length}
      isExpanded={isExpanded}
      onToggleMore={() => setIsExpanded((value) => !value)}
      isHiddenOnPhone={isHiddenOnPhone}
      testId="posts-lane-ready"
    >
      {groups.map((group) => (
        <React.Fragment key={group.catalogItemUuid ?? '-'}>
          <div className="d-flex align-items-center gap-2">
            <span className={`${styles.groupHead} flex-grow-1`}>
              {songHeading(intl, group.catalogItemUuid === null ? undefined : songs[group.catalogItemUuid], now)}
            </span>
            {group.catalogItemUuid !== null && !readOnly && (
              <button
                type="button"
                className={styles.link}
                onClick={() => actions.openPlan(group.catalogItemUuid as string)}
              >
                {intl.formatMessage(postsMessages.planThem)}
              </button>
            )}
          </div>
          {group.files.map((file) => (
            <PostsFileRow
              key={file.key}
              file={file}
              detail={joinDetail([rolloutDayText(intl, file.rolloutOffsetDays), madeText(intl, file.createdAt, now)])}
              selection={selectionOf(file)}
              onOpen={onOpen}
              right={
                <>
                  {file.inReview && (
                    <span className={`${styles.pill} ${styles.pillWarn}`}>
                      {intl.formatMessage(postsMessages.review)}
                    </span>
                  )}
                  {!readOnly && file.inReview && (
                    <button type="button" className={styles.link} onClick={() => actions.review(file)}>
                      {intl.formatMessage(postsMessages.review)}
                    </button>
                  )}
                  {!readOnly && !file.inReview && actions.canPost(file) && (
                    <button type="button" className={styles.link} onClick={() => actions.post(file)}>
                      {intl.formatMessage(postsMessages.postIt)}
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      className={`${styles.link} ${styles.hoverAction}`}
                      onClick={() => onArchive(file)}
                    >
                      {intl.formatMessage(postsMessages.archive)}
                    </button>
                  )}
                </>
              }
            />
          ))}
        </React.Fragment>
      ))}
    </PostsLane>
  );
};

export const PostsReadyLane = TypedMemo(PostsReadyLaneComponent);
